import { useEffect, useState } from "react";
import { Menu, X, Facebook, Instagram } from "lucide-react";
import { FB_URL, IG_URL } from "@/components/site/social";

const links = [
  { id: "narrativ", label: "Tilgang" },
  { id: "manifest", label: "Manifest" },
  { id: "ydelser", label: "Ydelser" },
  { id: "om", label: "Om mig" },
  { id: "kontakt", label: "Kontakt" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-[#F9F6F0]/85 backdrop-blur-md border-b border-cm-line"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-10 lg:px-16 flex items-center justify-between h-16 sm:h-20">
        <a
          href="#top"
          data-testid="logo-link"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3 group flex-shrink-0"
        >
          <img
            src="/brand/cm-logo.jpg"
            alt="CM Coach & Motivation"
            className="h-10 w-10 sm:h-12 sm:w-12 object-cover rounded-full ring-1 ring-cm-line transition-transform duration-700 group-hover:scale-105"
            data-testid="logo-image"
          />
        </a>

        <nav className="hidden md:flex items-center gap-6 lg:gap-8 xl:gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => handleNav(l.id)}
              className="nav-link whitespace-nowrap"
            >
              {l.label}
            </button>
          ))}
          <a
            href={FB_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-facebook"
            aria-label="Facebook"
            className="text-cm-ink-soft hover:text-cm-rose transition-colors duration-500"
          >
            <Facebook size={16} strokeWidth={1.5} />
          </a>
          <a
            href={IG_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram"
            aria-label="Instagram"
            className="text-cm-ink-soft hover:text-cm-rose transition-colors duration-500"
          >
            <Instagram size={16} strokeWidth={1.5} />
          </a>
          <button
            data-testid="nav-book-cta"
            onClick={() => handleNav("kontakt")}
            className="cm-btn !py-3 !px-6 !text-xs"
          >
            Book introsamtale
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 text-cm-ink"
          aria-label="Menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-cm-cream border-t border-cm-line"
        >
          <div className="flex flex-col px-6 py-6 gap-4">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="text-left nav-link"
              >
                {l.label}
              </button>
            ))}
            <a
              href={FB_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-facebook"
              className="nav-link flex items-center gap-2"
            >
              <Facebook size={14} strokeWidth={1.5} />
              Facebook
            </a>
            <a
              href={IG_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-instagram"
              className="nav-link flex items-center gap-2"
            >
              <Instagram size={14} strokeWidth={1.5} />
              Instagram
            </a>
            <button
              data-testid="mobile-nav-book-cta"
              onClick={() => handleNav("kontakt")}
              className="cm-btn !py-3 !text-xs justify-center"
            >
              Book introsamtale
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
